import React, { useMemo, useCallback } from 'react';
import { useForm } from 'react-hook-form';

import { useT } from '@transifex/react';

import { SectionTitle } from '../../../common/Titles/SectionTitle';
import { SectionSubtitle } from '../../../common/Titles/SectionSubtitle';
import { Button } from '../../../common/Button';

import s from './Summary.module.scss';

import { constructTestId } from '../../../../utils/test-ids/';
import { CREATE_CAMPAIGN_PAGE } from '../../../../constants/test-ids/create-campaing-sections/create-campaign-sections';
import { useDispatch } from 'react-redux';
import { AdSetSummary } from './AdSetSummary';
import { AdSummary } from './AdSummary';
import { getSelectedVideoAdPrice } from '../../../../utils/media';
import { setModalContent } from '../../../../store/ui';
import { campaignConstants } from '../../../../constants';

const SECTION = CREATE_CAMPAIGN_PAGE.SUMMARY;

const Summary = ({
  isArchive,
  publishCampaign,
  brandInfo,
  deactivateAll,
  campaign,
  toggleModalByName,
  isNotEnoughFunds,
  setModalDisplayMode,
  isProcessing,
  unpublishedChanges,
}) => {
  const dispatch = useDispatch();

  const hasUnpublishedChanges = useMemo(
    () => unpublishedChanges.length && !campaign.isDraft,
    [unpublishedChanges, campaign.isDraft]
  );

  const t = useT();

  const isBrandVerified = useMemo(
    () => brandInfo.accountStatus === 'verified',
    [brandInfo.accountStatus]
  );

  const allAdSets = useMemo(() => campaign.adSets, [campaign.adSets]);

  const { handleSubmit } = useForm({
    defaultValues: {},
  });

  const {
    id: campaignId,
    name: campaignName,
    category,
    campaignBrandName,
    campaignBrandLogoUri,
    selectedCharity,
  } = campaign;

  const isDifferentVideoLengthThanTheSelectedRange = useMemo(() => {
    if (brandInfo.role !== 'WeAre8') {
      const adSetsThatHaveAtLeastOneVideoOutOfRange = allAdSets.map((adSet) => {
        const adSetPricePerMessage = adSet?.incentive?.pricePerMessage;

        const adsPricingDetails =
          brandInfo.videoAdPricingData?.length > 0
            ? adSet.ads.map((ad) =>
                getSelectedVideoAdPrice(brandInfo.videoAdPricingData, ad?.videoLength)
              )
            : [];

        const atLeastOneVideoOutOfRange = adsPricingDetails.some(
          (adPricingDetails) => adPricingDetails?.pricePerMessage !== adSetPricePerMessage
        );

        return atLeastOneVideoOutOfRange;
      });

      return adSetsThatHaveAtLeastOneVideoOutOfRange.some((value) => value);
    } else return false;
  }, [allAdSets, brandInfo.role, brandInfo.videoAdPricingData]);

  const handlePublish = useCallback(() => {
    if (isDifferentVideoLengthThanTheSelectedRange && brandInfo.role !== 'WeAre8') {
      dispatch(toggleModalByName({ name: 'default', value: true }));
      dispatch(
        setModalContent({
          name: 'default',
          content: {
            title:
              'Some of your creatives do not match with the selected pricing range of the Ad Set. Please change it.',
            withSubmit: true,
            cancelText: 'Got it',
          },
        })
      );
      return;
    }
    if (isNotEnoughFunds) {
      dispatch(toggleModalByName({ name: 'campaignActivation', value: true }));
      return;
    }
    dispatch(publishCampaign(campaignId));
  }, [
    isDifferentVideoLengthThanTheSelectedRange,
    brandInfo.role,
    isNotEnoughFunds,
    dispatch,
    publishCampaign,
    campaignId,
    toggleModalByName,
  ]);

  const atLeastOneDraftAd = useMemo(
    () => campaign.adSets.some((adSet) => adSet.ads.some((ad) => ad.isDraft)),
    [campaign.adSets]
  );

  const dirtyAd = allAdSets.find((adSet) =>
    adSet.ads.find((ad) => ad.isDirty || ad.adVideo === '')
  );

  const isPublishDisabled = useMemo(() => {
    return !dirtyAd
      ? (isProcessing || !isBrandVerified || !atLeastOneDraftAd) && !hasUnpublishedChanges
      : Boolean(dirtyAd);
  }, [dirtyAd, isProcessing, isBrandVerified, atLeastOneDraftAd, hasUnpublishedChanges]);

  const selectedCategory = useMemo(
    () => campaignConstants.objectives.find((item) => item.value === category),
    [category]
  );

  return (
    <div className={s['summary']}>
      <SectionTitle>
        <h3>{t('Campaign Summary')}</h3>
      </SectionTitle>
      <SectionSubtitle>
        {t(
          'Review your campaign details. Summary displays only your saved changes and ad sets that have at least one saved ad.'
        )}
      </SectionSubtitle>

      <form onSubmit={handleSubmit(handlePublish)}>
        <div className={s['summary__brand-info']}>
          <div className={s['image-section']}>
            <div className={s['image-section__picture']}>
              <img className={s['img']} src={campaignBrandLogoUri} alt="brand logo" />
            </div>
            <div className={s['image-section__name']}>{campaignBrandName || t(`Brand Name`)}</div>
          </div>
        </div>
        <div className={s['summary__campaign-level']}>
          <div className={s['summary__section']}>
            <div className={s['summary__section-title']}>{t('Campaign Name')}</div>
            <div className={s['summary__section-content']}>
              <div className={s['summary__section-content-block']}>
                {campaignName || t(`My Campaign`)}
              </div>
            </div>
          </div>

          <div className={s['summary__section']}>
            <div className={s['summary__section-title']}>{t('Objective')}</div>
            <div className={s['summary__section-content']}>
              <div className={s['summary__section-content-block']}>
                <div className={s['accented']}>{selectedCategory?.title}</div>
                {selectedCategory?.subtitle ? selectedCategory?.subtitle : ''}
              </div>
            </div>
          </div>

          <div className={s['summary__section']}>
            <div className={s['summary__section-title']}>{t('Selected Charity')}</div>
            <div className={s['summary__section-content']}>
              <div className={s['summary__section-content-block']}>
                <div className={s['image-section']}>
                  <div className={s['image-section__picture']}>
                    <img
                      className={s['img']}
                      src={selectedCharity?.logoUri || ''}
                      alt="charity logo"
                    />
                  </div>
                  <div className={s['image-section__name']}>{selectedCharity?.name || ''}</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className={s['summary__adSets-level']}>
          {allAdSets.map((adSet) => (
            <AdSetSummary adSet={adSet} brandInfo={brandInfo} key={adSet.id}>
              <div className={s['summary__ads-level']}>
                {adSet.ads.map((ad) => (
                  <AdSummary ad={ad} adSet={adSet} brandInfo={brandInfo} key={ad.id} />
                ))}
              </div>
            </AdSetSummary>
          ))}
        </div>
        {!isArchive && (
          <>
            <div className={s['buttons']}>
              <Button
                data-testid={constructTestId(SECTION, 'publish-btn')}
                customText={t('Publish *')}
                type="submit"
                styling="primary"
                disabled={isPublishDisabled}
              />
            </div>

            <span className={isBrandVerified ? s['notice'] : s['warning']}>
              *{' '}
              {isBrandVerified
                ? `By clicking 'Publish' you are sending your campaign to the WeAre8 Moderation Team for approval.`
                : `You need to have a verified account to be able to publish campaigns.`}
            </span>
          </>
        )}
      </form>
    </div>
  );
};

export default Summary;
